import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Banner, Button, Icon, Spinner, Tag, TextArea, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  nextApi,
  type AutomationCatalogDto,
  type AutomationDesignDto,
  type AutomationDesignMessage,
  type AutomationInput,
} from "../../api/nextApi";
import { AiFeedbackButtons } from "../ai/feedback/AiFeedbackButtons";
import { loadAssistantConfigured } from "../assistant/AssistantPanel";
import { notifyAutomationsChanged } from "../events";
import { useShell } from "../shell/ShellContext";
import type { AutomationDraft } from "./AutomationEditor";

/**
 * «יצירה עם AI» (ADR-0035) — Turbo's AutomationAiChat: describe in words what should happen by itself,
 * the designer answers with a validated draft (or questions / an existing match). Nothing is created
 * until the user presses «הפעלה» on the proposal card.
 */
type Turn =
  | { role: "user"; text: string }
  | { role: "assistant"; text: string; result: AutomationDesignDto; created?: string | null; ref: string; question: string }
  | { role: "error"; text: string };

const EXAMPLES: Record<string, string[]> = {
  customers: ["כשנוסף לקוח חדש — לשלוח לו מייל ברוכים הבאים", "לפתוח משימת שיחת היכרות לכל לקוח חדש תוך יומיים"],
  subscriptions: ["כל יום ראשון בבוקר לשלוח תזכורת ללקוחות עם חוב", "חודש לפני שהרשאה לחיוב פגה — לפתוח לי משימה"],
  finance: ["כל יום חמישי לשלוח לי דוח הכנסות והוצאות של השבוע", "כשנרשמת הוצאה מעל 5,000 ₪ — לשלוח לי מייל"],
  payment_requests: ["כל בוקר לשלוח לי רשימה של דרישות תשלום פתוחות"],
  tasks: ["כל בוקר לשלוח לכל אחראי את המשימות שלו שבאיחור", "כשנוצרת משימה דחופה — לשלוח מייל לאחראי"],
  forms: ["כשמוגש טופס — לפתוח משימה לטיפול", "כשמוגש טופס — לשלוח לי מייל עם התשובות"],
  meetings: ["כל יום ראשון לשלוח לי תמונת מצב שבועית"],
  home: ["כל יום ראשון ב-08:00 לשלוח לי תמונת מצב"],
  general: ["כל יום ראשון ב-08:00 לשלוח לי תמונת מצב", "כל בוקר לשלוח לי את המשימות שבאיחור"],
};

const ERRORS: Record<string, string> = {
  AI_BUDGET_EXCEEDED: "נגמרו הקרדיטים של הארגון ל-AI החודש. מנהל הארגון יכול לבקש קרדיטים נוספים בהגדרות.",
  AI_NOT_ALLOWED: "לא הוגדרה לך הרשאה להשתמש ב-AI בארגון. אפשר לבקש ממנהל הארגון.",
  AI_USER_LIMIT: "הגעת למגבלת השימוש האישית שלך ב-AI החודש.",
  AI_DISABLED: "השימוש ב-AI כבוי בארגון.",
  AI_FEATURE_DISABLED: "מעצב האוטומציות כבוי כרגע.",
  AI_FEATURE_BUDGET: "התקציב של מעצב האוטומציות לחודש הזה נוצל.",
  AI_NOT_CONFIGURED: "ה-AI לא מוגדר בשרת.",
  AI_RATE_LIMITED: "יותר מדי בקשות בדקה האחרונה — נסו שוב בעוד רגע.",
};

/** ai.use + automations.manage + a configured provider */
export function useAutomationAiAvailable(): boolean {
  const { allowed } = useShell();
  const may = allowed("ai.use") && allowed("automations.manage");
  const [configured, setConfigured] = useState(false);
  useEffect(() => {
    if (!may) return undefined;
    let alive = true;
    void loadAssistantConfigured().then((v) => alive && setConfigured(v));
    return () => {
      alive = false;
    };
  }, [may]);
  return may && configured;
}

const toDraft = (a: AutomationInput): AutomationDraft => ({ ...a, status: a.status === "paused" ? "paused" : "active" });

export function AutomationAiChat({
  screenKey,
  catalog,
  initialPrompt,
  onEdit,
  onOpenAutomation,
  onCreated,
}: {
  screenKey: string;
  catalog: AutomationCatalogDto | null;
  initialPrompt?: string | null;
  onEdit: (draft: AutomationDraft) => void;
  onOpenAutomation?: (id: string) => void;
  onCreated?: () => void;
}) {
  const toast = useCkToast();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState<number | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const sentInitial = useRef<string | null>(null);

  const scrollDown = () =>
    window.requestAnimationFrame(() => logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" }));

  const lastDraft = (): AutomationInput | null => {
    for (let i = turns.length - 1; i >= 0; i--) {
      const t = turns[i]!;
      if (t.role === "assistant" && t.result.automation) return t.result.automation;
    }
    return null;
  };

  const send = async (raw: string) => {
    const message = raw.trim();
    if (!message || busy) return;
    setText("");
    const history: AutomationDesignMessage[] = [
      ...turns.flatMap((t): AutomationDesignMessage[] =>
        t.role === "user" ? [{ role: "user", text: t.text }] : t.role === "assistant" ? [{ role: "assistant", text: t.text }] : [],
      ),
      { role: "user" as const, text: message },
    ].slice(-14);
    setTurns((prev) => [...prev, { role: "user", text: message }]);
    setBusy(true);
    scrollDown();
    try {
      const result = await nextApi.automations.design({ screenKey, draft: lastDraft(), messages: history });
      setTurns((prev) => [...prev, { role: "assistant", text: result.reply, result, ref: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, question: message }]);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      setTurns((prev) => [...prev, { role: "error", text: ERRORS[err.code ?? ""] ?? err.message ?? "המעצב לא הצליח לענות כרגע" }]);
    } finally {
      setBusy(false);
      scrollDown();
    }
  };

  useEffect(() => {
    if (initialPrompt && sentInitial.current !== initialPrompt) {
      sentInitial.current = initialPrompt;
      void send(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt]);

  const firstRequest = turns.find((t) => t.role === "user")?.text ?? null;

  const activate = async (index: number, a: AutomationInput) => {
    setCreating(index);
    try {
      const created = await nextApi.automations.create({ ...a, status: "active", createdVia: "ai", aiPrompt: firstRequest });
      toast.success(`„${created.name}” הופעלה`);
      notifyAutomationsChanged();
      setTurns((prev) => prev.map((t, i) => (i === index && t.role === "assistant" ? { ...t, created: created.id } : t)));
      onCreated?.();
    } catch (e) {
      toast.error((e as { message?: string })?.message || "ההפעלה נכשלה");
    } finally {
      setCreating(null);
    }
  };

  const addTemplate = async (key: string) => {
    try {
      const created = await nextApi.automations.fromTemplate(key);
      toast.success(`„${created.name}” הופעלה`);
      notifyAutomationsChanged();
      onCreated?.();
    } catch (e) {
      toast.error((e as { message?: string })?.message || "ההפעלה נכשלה");
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(text);
    }
  };

  const examples = EXAMPLES[screenKey] ?? EXAMPLES.general!;
  const screenLabel = (key: string) => catalog?.screens.find((s) => s.key === key)?.label ?? key;

  return (
    <div className="atm-ai">
      <div className="atm-ai__log" ref={logRef}>
        {turns.length === 0 ? (
          <div className="atm-ai__intro">
            <span className="atm-ai__logo">
              <Icon name="sparkle" size={22} />
            </span>
            <b>מה תרצו שיקרה לבד?</b>
            <p>כתבו במילים שלכם — למשל מתי, למי ומה לשלוח. אכין טיוטה מדויקת לפי מה שהמערכת יודעת לעשות, ושום דבר לא יופעל בלי אישור שלכם.</p>
            <div className="atm-ai__examples">
              {examples.map((ex) => (
                <button key={ex} type="button" className="atm-chip" onClick={() => void send(ex)}>
                  {ex}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {turns.map((t, i) => {
          if (t.role === "user") {
            return (
              <div key={i} className="atm-msg atm-msg--user">
                <p className="atm-msg__text">{t.text}</p>
              </div>
            );
          }
          if (t.role === "error") {
            return (
              <Banner key={i} tone="danger">
                {t.text}
              </Banner>
            );
          }
          const r = t.result;
          const isLast = i === turns.length - 1;
          return (
            <div key={i} className="atm-msg atm-msg--assistant">
              <AiFeedbackButtons surface="automation_designer" refKey={t.ref} question={t.question} answer={t.text} />
              {r.analysis.length ? (
                <details className="atm-analysis">
                  <summary>
                    <Icon name="search" size={12} /> בדקתי בנתונים ({r.analysis.length})
                  </summary>
                  <ul>
                    {r.analysis.map((x, k) => (
                      <li key={k}>
                        {x.label}: {x.summary}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
              {t.text ? <p className="atm-msg__text">{t.text}</p> : null}

              {r.status === "unsupported" ? (
                <Banner tone="warning">את זה עוד אי אפשר לעשות באוטומציה. אפשר לנסח בקשה אחרת — מייל, משימה או דוח לפי לוח זמנים או כשמשהו קורה.</Banner>
              ) : null}

              {r.questions.length ? (
                <div className="atm-chips">
                  {r.questions.map((q) => (
                    <button key={q} type="button" className="atm-chip" disabled={busy || !isLast} onClick={() => setText(`${q} `)}>
                      {q}
                    </button>
                  ))}
                </div>
              ) : null}

              {r.matches.length ? (
                <div className="atm-matches">
                  {r.matches.map((m) => (
                    <div key={`${m.type}-${m.id}`} className="atm-match">
                      <Icon name={m.type === "automation" ? "sparkle" : "copy"} size={16} />
                      <div className="atm-match__text">
                        <small>{m.type === "automation" ? "כבר קיימת אוטומציה" : "יש תבנית מוכנה"}</small>
                        <b>{m.name}</b>
                        {m.reason ? <span>{m.reason}</span> : null}
                      </div>
                      {m.type === "automation" ? (
                        onOpenAutomation ? (
                          <Button size="sm" variant="ghost" onClick={() => onOpenAutomation(m.id)}>
                            פתיחה
                          </Button>
                        ) : null
                      ) : (
                        <Button size="sm" variant="ghost" icon="plus" onClick={() => void addTemplate(m.id)}>
                          הפעלה
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : null}

              {r.automation ? (
                <div className={`atm-proposal${t.created ? " is-done" : ""}`}>
                  <div className="atm-proposal__head">
                    <Icon name="sparkle" size={15} />
                    <b>{r.automation.name}</b>
                    <Tag tone="info">{screenLabel(r.automation.screenKey)}</Tag>
                    {t.created ? <Tag tone="success">הופעלה</Tag> : <Tag tone="neutral">טיוטה</Tag>}
                  </div>
                  {r.summary ? (
                    <p className="atm-proposal__line">
                      <Icon name="repeat" size={13} /> {r.summary}
                    </p>
                  ) : null}
                  {r.automation.description ? <p className="atm-proposal__line">{r.automation.description}</p> : null}
                  {r.preview ? (
                    <p className="atm-proposal__line">
                      <Icon name="users" size={13} />
                      {r.preview.matched
                        ? `כרגע ${r.preview.matched.toLocaleString("he-IL")} רשומות מתאימות${r.preview.sample.length ? ` (למשל ${r.preview.sample.join(", ")})` : ""}`
                        : "כרגע אין רשומות מתאימות — האוטומציה תפעל כשיהיו"}
                    </p>
                  ) : null}
                  {r.automation.actions
                    .filter((a): a is Extract<typeof a, { type: "send_email" }> => a.type === "send_email")
                    .slice(0, 1)
                    .map((a, k) => (
                      <details key={k} className="atm-proposal__msg">
                        <summary>המייל שיישלח: {a.subject}</summary>
                        <p>{a.body}</p>
                      </details>
                    ))}
                  {r.insights.length ? (
                    <div className="atm-insights">
                      <b>
                        <Icon name="info" size={13} />
                        שימו לב
                      </b>
                      <ul>
                        {r.insights.map((x, k) => (
                          <li key={k}>{x}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {!t.created ? (
                    <div className="atm-proposal__actions">
                      <Button size="sm" variant="primary" icon="check" disabled={creating !== null} onClick={() => void activate(i, r.automation!)}>
                        {creating === i ? "מפעיל…" : "הפעלה"}
                      </Button>
                      <Button size="sm" variant="ghost" icon="edit" onClick={() => onEdit(toDraft(r.automation!))}>
                        עריכה לפני הפעלה
                      </Button>
                    </div>
                  ) : onOpenAutomation ? (
                    <div className="atm-proposal__actions">
                      <Button size="sm" variant="ghost" onClick={() => onOpenAutomation(t.created!)}>
                        פתיחת האוטומציה
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : r.insights.length ? (
                <div className="atm-insights">
                  <ul>
                    {r.insights.map((x, k) => (
                      <li key={k}>{x}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          );
        })}

        {busy ? (
          <div className="atm-msg atm-msg--assistant atm-msg--busy">
            <Spinner size={16} /> מתכנן את האוטומציה…
          </div>
        ) : null}
      </div>
      <div className="atm-ai__input">
        <TextArea
          rows={2}
          value={text}
          maxLength={4000}
          placeholder="מה האוטומציה צריכה לעשות? (Enter לשליחה)"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy}
          aria-label="תיאור האוטומציה"
        />
        <Button variant="primary" icon="left" iconOnly aria-label="שליחה" disabled={busy || !text.trim()} onClick={() => void send(text)} />
      </div>
    </div>
  );
}

export default AutomationAiChat;
